import { createContext, use, useCallback, useMemo, useRef, useState } from 'react';

import type { MenuItem } from '@/consts/navigation';
import { useLocation } from 'react-router';

interface NavigationContextValue {
  expandedSections: Set<string>;
  toggleSection: (sectionText: string) => void;
  expandSection: (sectionText: string) => void;
  isRouteActive: (route: string | undefined) => boolean;
  hasActiveChild: (children: MenuItem[] | undefined) => boolean;
  isNavCollapsed: boolean;
  toggleNavCollapse: () => void;
  isMobileOpen: boolean;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export const useNavigationContext = () => {
  const context = use(NavigationContext);
  if (!context) {
    throw new Error('useNavigationContext must be used within NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: React.ReactNode;
}

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set());
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  const activeChildCacheRef = useRef<Map<string, boolean>>(new Map());
  const prevLocationRef = useRef({ pathname: location.pathname, search: location.search });

  if (prevLocationRef.current.pathname !== location.pathname || prevLocationRef.current.search !== location.search) {
    activeChildCacheRef.current.clear();
    prevLocationRef.current = { pathname: location.pathname, search: location.search };
  }

  const toggleNavCollapse = useCallback(() => {
    setIsNavCollapsed((prev) => !prev);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  const toggleSection = useCallback((sectionText: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionText)) {
        newSet.delete(sectionText);
      } else {
        newSet.add(sectionText);
      }
      return newSet;
    });
  }, []);

  const expandSection = useCallback((sectionText: string) => {
    setExpandedSections((prev) => {
      if (prev.has(sectionText)) return prev;
      const newSet = new Set(prev);
      newSet.add(sectionText);
      return newSet;
    });
  }, []);

  const pathname = location.pathname;
  const search = location.search;
  const fullPath = pathname + search;

  const isRouteActive = useCallback(
    (route: string | undefined) => {
      if (!route) return false;
      // Exact match including query params (for legacy routes)
      if (fullPath === route) return true;
      // Exact pathname match (for native routes without query params)
      if (pathname === route) return true;
      // Starts-with match for nested routes
      return pathname.startsWith(route + '/');
    },
    [pathname, fullPath]
  );

  const hasActiveChild = useCallback(
    (children: MenuItem[] | undefined) => {
      if (!children || children.length === 0) return false;

      const cacheKey = children.map((c) => c.to ?? '').join('|');
      const cached = activeChildCacheRef.current.get(cacheKey);

      if (cached !== undefined) return cached;

      const checkChildren = (items: MenuItem[]): boolean => {
        for (const child of items) {
          if (isRouteActive(child.to)) return true;
          if (child.children && checkChildren(child.children)) return true;
        }
        return false;
      };

      const result = checkChildren(children);
      activeChildCacheRef.current.set(cacheKey, result);
      return result;
    },
    [isRouteActive]
  );

  const value = useMemo(
    () => ({
      expandedSections,
      toggleSection,
      expandSection,
      isRouteActive,
      hasActiveChild,
      isNavCollapsed,
      toggleNavCollapse,
      isMobileOpen,
      toggleMobileMenu,
      closeMobileMenu
    }),
    [
      expandedSections,
      toggleSection,
      expandSection,
      isRouteActive,
      hasActiveChild,
      isNavCollapsed,
      toggleNavCollapse,
      isMobileOpen,
      toggleMobileMenu,
      closeMobileMenu
    ]
  );

  return <NavigationContext value={value}>{children}</NavigationContext>;
};
